import { usePermissions } from "@/context/permissions-context"
import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query"

interface ProtectedQueryOptions<TData, TError> extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  queryKey: unknown[]
  queryFn: () => Promise<TData>
  requiredPermissions?: string[]
  requireAll?: boolean
}

type ProtectedQueryResult<TData, TError> = UseQueryResult<TData, TError> & {
  hasPermission: boolean
  /** False while the session is still resolving — the permission answer is not known yet. */
  isPermissionsReady: boolean
}

export function useProtectedQuery<TData = unknown, TError = unknown>({
  queryKey,
  queryFn,
  requiredPermissions = [],
  requireAll = true,
  ...options
}: ProtectedQueryOptions<TData, TError>): ProtectedQueryResult<TData, TError> {
  const { hasAllPermissions, hasAnyPermission, isReady } = usePermissions()

  const granted = requiredPermissions.length === 0
    ? true
    : requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)

  const result = useQuery<TData, TError>({
    ...options,
    queryKey,
    queryFn,
    // Apply after spread so caller's enabled cannot override permission check
    enabled: isReady && granted && (options.enabled ?? true),
  })

  return {
    ...result,
    // Before the session resolves nothing is denied yet, and the query is still
    // pending — callers keep showing their loader instead of an access-denied screen.
    isLoading: !isReady || result.isLoading,
    isPending: !isReady || result.isPending,
    hasPermission: isReady ? granted : true,
    isPermissionsReady: isReady,
  } as ProtectedQueryResult<TData, TError>
}
